import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  NavBar, 
  Card, 
  Button, 
  Form, 
  Input, 
  TextArea,
  Selector,
  Space,
  Toast,
  Rate
} from 'antd-mobile';
import { 
  LeftOutline, 
  MessageOutline, 
  SendOutline,
  StarOutline,
  EditSOutline,
  HeartOutline
} from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';

export default function Feedback() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const feedbackTypes = [
    { label: '问题反馈', value: 'bug' },
    { label: '功能建议', value: 'feature' },
    { label: '改进建议', value: 'improvement' },
    { label: '投诉建议', value: 'complaint' },
    { label: '表扬建议', value: 'praise' },
    { label: '其他', value: 'other' }
  ];

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      // 这里应该调用实际的反馈提交API
      console.log('反馈内容:', values);
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      Toast.show({
        icon: 'success',
        content: '提交成功',
        duration: 2000
      });
      
      // 清空表单
      form.resetFields();
      
      // 延迟返回
      setTimeout(() => {
        navigate('/profile');
      }, 1000);
      
    } catch (error) {
      console.error('提交反馈失败:', error);
      Toast.show({
        icon: 'fail',
        content: '提交失败'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <MessageOutline className="mr-2" />
            反馈建议
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 反馈说明 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-4"
              >
                <MessageOutline className="text-white text-2xl" />
              </motion.div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                反馈建议
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                我们非常重视您的意见和建议，请详细描述您遇到的问题或建议
              </p>
            </div>
          </Card>
        </motion.div>

        {/* 反馈表单 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3" />
                <EditSOutline className="mr-2" />
                填写反馈
              </h3>
              
              <Form form={form} layout="vertical">
                {/* 反馈类型 */}
                <Form.Item 
                  name="type" 
                  label="反馈类型"
                  rules={[{ required: true, message: '请选择反馈类型' }]}
                >
                  <Selector
                    options={feedbackTypes}
                    className="rounded-xl"
                    style={{
                      '--checked-color': '#3b82f6',
                      '--border': '1px solid #e5e7eb',
                      '--border-radius': '12px',
                      '--padding': '12px 16px'
                    }}
                  />
                </Form.Item>
                
                {/* 反馈标题 */}
                <Form.Item 
                  name="title" 
                  label="反馈标题"
                  rules={[
                     { required: true, message: '请输入反馈标题' },
                     { min: 5, message: '标题至少5个字符' },
                     { max: 100, message: '标题最多100个字符' }
                   ]}
                >
                  <Input 
                    placeholder="请简要描述您的问题或建议"
                    maxLength={100}
                    className="rounded-xl"
                  />
                </Form.Item>
                
                {/* 详细描述 */}
                <Form.Item 
                  name="content" 
                  label="详细描述"
                  rules={[
                     { required: true, message: '请输入详细描述' },
                     { min: 10, message: '描述至少10个字符' },
                     { max: 1000, message: '描述最多1000个字符' }
                   ]}
                >
                  <TextArea 
                    placeholder="请详细描述您遇到的问题或建议，包括具体的操作步骤、期望结果等"
                    rows={6}
                    showCount
                    maxLength={1000}
                    className="rounded-xl"
                    style={{ '--font-size': '14px' }}
                  />
                </Form.Item>
                
                {/* 满意度评分 */}
                <Form.Item 
                  name="rating" 
                  label="满意度评分"
                  rules={[{ required: true, message: '请选择满意度评分' }]}
                >
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="text-center p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-100"
                  >
                    <Rate 
                       allowHalf
                       character={<StarOutline />}
                       style={{ '--star-size': '32px', '--active-color': '#f59e0b' }}
                     />
                    <p className="text-gray-500 text-sm mt-3">
                      请为我们的服务打分
                    </p>
                  </motion.div>
                </Form.Item>
                
                {/* 联系方式（可选） */}
                <Form.Item 
                  name="contact" 
                  label="联系方式（可选）"
                  rules={[
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input 
                    placeholder="请输入您的邮箱地址，以便我们回复您"
                    type="email"
                    className="rounded-xl"
                  />
                </Form.Item>
              </Form>
            </div>
          </Card>
        </motion.div>

        {/* 提交须知 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-1 h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3" />
                <HeartOutline className="mr-2" />
                提交须知
              </h3>
              <div className="space-y-3">
                {[
                  '我们会认真对待每一条反馈',
                  '请提供详细的问题描述以便我们更好地帮助您',
                  '如果涉及敏感信息，请谨慎填写',
                  '我们会在3个工作日内回复您的反馈'
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                    className="flex items-center text-gray-600 text-sm"
                  >
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mr-3 flex-shrink-0" />
                    {item}
                  </motion.div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="mt-6 space-y-3"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              color="primary"
              block
              size="large"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 rounded-xl h-12 font-medium shadow-lg"
            >
              <SendOutline className="mr-2" />
              {submitting ? '提交中...' : '提交反馈'}
            </Button>
          </motion.div>
          
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              color="default"
              fill="outline"
              block
              onClick={() => navigate('/profile')}
              disabled={submitting}
              className="border-gray-200 text-gray-600 rounded-xl h-12 font-medium"
            >
              返回
            </Button>
          </motion.div>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
}